import { Box, Stack, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { formatAddress } from "encamp-shared/src/utils/address";
import { WasteManifestSource } from "generated-graphql/graphql";

import { SOURCE_COLOR, SOURCE_LABEL } from "../manifestSourceDisplay";
import { SectionHeader } from "./SectionHeader";
import { ManifestDetail, ManifestTransporter } from "./types";

interface TransportersSectionProps {
  transporters: NonNullable<ManifestDetail["wasteTransporters"]>;
  /** The source the transporter list originates from (uniform across rows). */
  source: WasteManifestSource;
}

function buildColumns(source: WasteManifestSource): CustomGridColDef<ManifestTransporter>[] {
  return [
    { field: "transporterLineNumber", headerName: "#", width: 50 },
    { field: "transporterName", headerName: "Name", flex: 1, minWidth: 120 },
    { field: "transporterId", headerName: "EPA ID", width: 130 },
    { field: "daysInPossession", headerName: "Days in Possession", width: 140 },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 150,
      valueGetter: (_value, row) =>
        formatAddress(
          [row.vendorAddressLine1, row.vendorAddressLine2].filter(Boolean).join(" "),
          row.vendorCity,
          row.vendorState,
          row.vendorZip
        ),
    },
    {
      field: "source",
      headerName: "Source",
      width: 120,
      sortable: false,
      renderCell: () => (
        <Stack direction="row" spacing={0.75} alignItems="center" height="100%">
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flexShrink: 0,
              backgroundColor: SOURCE_COLOR[source],
            }}
          />
          <Typography variant="body2">{SOURCE_LABEL[source]}</Typography>
        </Stack>
      ),
    },
  ];
}

export function TransportersSection({ transporters, source }: TransportersSectionProps) {
  if (transporters.length === 0) return null;

  return (
    <Stack spacing={0}>
      <SectionHeader title="Transporters" />
      <DataGrid
        rows={transporters}
        columns={buildColumns(source)}
        getRowId={(row) => row.transporterLineNumber}
        disableRowSelectionOnClick
        hideFooter
        autoHeight
      />
    </Stack>
  );
}
